import axios, { AxiosResponse, AxiosError } from "axios";
import { getCookie } from "@util/cookie";
import {
  Candidate,
  WorkspaceCompanyRequest,
  SearchCandidateFilter,
  SendCustomizedEmailRequest,
  SendCustomizedEmailResponse,
} from "@type/candidate";
import { catchErrorLog, fatalErrorLog, serializeRequestPayload } from "@util/sentry";
import { CalculateSimiliarityScoreParams, JobOwnerItem } from "@type/job";
import { EmailTemplateRequest } from "@type/setting";
import { WorkSpaceCompanyListRequest } from "@type/company";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

// Define types for dashboard data
export interface DashboardStat {
  value: number;
  change_percent: number;
  change_text: string;
}

export interface EmployerDashboardData {
  total_candidates: DashboardStat;
  active_jobs: {
    value: number;
    new_this_week: number;
    change_text: string;
  };
  interviews_completed: DashboardStat;
  hiring_rate: DashboardStat;
}

export interface EmployerDashboardResponse {
  success: boolean;
  data?: {
    code: number;
    message: string;
    data: EmployerDashboardData;
    errors: string[] | null;
  };
  status?: number;
  message?: string;
  error?: any;
}

export interface ConfirmSettingsData {
  publicVisibility: boolean;
  disableApplications: boolean;
  sendAutomatedCommunications: boolean;
  listingOwners: string[];
  enableAutomatedRejections: boolean;
  daysUntilAutoRejection: number;
  rejectionEmailSubject: string;
  rejectionEmailContents: string;
}

export interface LocationMetadata {
  formattedAddress: string;
  googleMapsUri: string;
  country: string;
}

// Job creation types
export interface JobCreateRequest {
  id?: string; // job id for editing existing job
  job_title: string;
  hr_id?: string;
  hr_name?: string;
  department?: string;
  company_name?: string;
  work_arrangement?: string;
  location?: string;
  job_type?: string;
  salary_range?: string;
  job_description: string;
  interview_type?: string;
  // Per-job opt-in: allow the AI to interview candidates on this job by phone
  // call (#3031). Persisted on the job; the BE returns/accepts it as
  // `is_enabled_wa_call` (verified on /jobs/detail).
  is_enabled_wa_call?: boolean;
  requirements?: string;
  benefits?: string;
  pay_rate_frequency?: string;
  rate_currency?: string;
  fixed_rate?: number;
  min_rate?: number;
  max_rate?: number;
  referral_bonus?: number;
  referral_currency?: string;
  job_status?: string; // Added job_status field
  assessment_ids?: { type: string; id: string; name: string }[]; // Updated to match backend field name
  location_metadata?: LocationMetadata | null;
  job_latitude: number;
  job_longitude: number;

  is_public?: boolean;
  is_send_auto_communication?: boolean;
  is_enable_auto_rejection?: boolean;
  is_enable_work_authorization?: boolean;
  is_enable_application_expiry?: boolean;
  application_expiry_days?: number;
  days_auto_rejection?: number;
  rejection_email_subject?: string;
  rejection_email_body?: string;
  owner_ids?: string[];
  is_collaborator?: boolean;
  listing_owners?: JobOwnerItem[]; // user IDs of listing owners
  outgoing_email?: {
    sender_name?: string;
    default_reply_to?: string;
  },
  work_authorization_metadata?: {
    collect_employment_status?: boolean;
    collect_expected_salary?: boolean;
    collect_start_availability?: boolean;
    collect_current_salary?: boolean;
    collect_relocation_willingness?: boolean;
    collect_weekend_shift_willingness?: boolean;
    collect_work_authorization?: boolean;
    collect_education_level?: boolean;
  };

  // Customizable Hiring Flow (#3066/#3070, BE contract #3641). Sent on
  // create/update; the job read returns it resolved (ourteam Default when
  // uncustomized) plus `flow_version` and the in-flight count for the
  // versioned-edit warning banner.
  hiring_flow?: import("@type/hiring-flow").HiringFlow;
  flow_version?: number;
  hiring_flow_in_flight_count?: number;
  // Per-company rollout flag (#3084, BE #3659) — read-only on the job detail;
  // absent (old BE) ⇒ OFF, so all flow UI stays hidden.
  company_hiring_flow_enabled?: boolean;
  // Chat-native WhatsApp flow rollout flag (#3090, BE #3697) — read-only; absent ⇒ OFF.
  company_chat_native_enabled?: boolean;
  // Company WhatsApp rollout flag — read-only on the job detail; absent ⇒ OFF.
  // Gates custom_message + the WA flow preview (#3257: the BE coerces a
  // whatsapp_first flow to web when this is off).
  company_whatsapp_enabled?: boolean;
  // Per-JOB "new hiring flow" flag (#3202, BE ourteam-backend#3913). Split default:
  // existing jobs OFF (server_default false), new jobs ON (ORM default true). Read on
  // job detail + accepted on create/update. Absent ⇒ treated as OFF by the FE.
  new_hiring_flow_enabled?: boolean;

  // admin
  masked_jd?: string;
  masked_requirements?: string;
  masked_benefits?: string;
}

export interface JobCreateResponse {
  success: boolean;
  data?: {
    code: number;
    message: string;
    data: any;
    errors: string[] | null;
  };
  status?: number;
  message?: string;
  error?: any;
}

/**
 * Fetches employer dashboard metrics
 * @returns Promise with dashboard metrics data
 */
export const getEmployerDashboard =
  async (): Promise<EmployerDashboardResponse> => {
    try {
      const authToken = getCookie("authToken");

      if (!authToken) {
        return {
          success: false,
          message: "Authentication required",
        };
      }

      const response: AxiosResponse = await axios({
        url: `${apiURL}/employer/dashboard`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        timeout: 10000, // 10 second timeout
      });

      return {
        success: true,
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      const err = error as AxiosError;
      const responseData = err.response?.data as Record<string, unknown>;

      return {
        success: false,
        status: err.response?.status || 500,
        message:
          (responseData?.message as string) ||
          (err.code === "ECONNABORTED"
            ? "Request timed out"
            : "Failed to fetch dashboard data"),
        error: err,
      };
    }
  };

/**
 * Creates a new job posting
 * @param jobData - The job details to create
 * @returns Promise with job creation response
 */
export const createJob = async (
  jobData: JobCreateRequest
): Promise<JobCreateResponse> => {
  // Declared outside the try so the catch can report the exact body that was
  // sent, not the raw argument — they differ (assessment_ids is normalized).
  const jobRequestData = {
    ...jobData,
    assessment_ids: jobData.assessment_ids || null, // Using the updated field name
  };

  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return {
        success: false,
        message: "Authentication required",
      };
    }

    // Use the proxy URL instead of the full API URL
    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/post`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      data: jobRequestData,
      // set 2min timeout
      timeout: 120000,
    });

    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    // Was catchErrorLog(`… ${error.message}`), which for an axios failure reads
    // "Request failed with status code 401" — dropped by the 401/404 filter, so
    // the two most common ways a job save dies left no record. The status moves
    // into the context, where nothing filters it. This also resolves rather than
    // rejecting, so a caller that only attaches `.catch()` never hears about it.
    fatalErrorLog(
      "[job] create failed — the job posting was not saved",
      {
        job_title: jobData?.job_title,
        http_status: err.response?.status ?? "none (network or timeout)",
        detail: (responseData?.message as string) || err.message,
        // The exact body that was sent, so the failure can be reproduced
        // without asking the HR to retype the posting they just lost.
        request_payload: serializeRequestPayload(jobRequestData),
      },
      { area: "job_write" },
    );

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Request timed out"
          : "Failed to create job posting"),
      error: err,
    };
  }
};

/**
 * Fetches job listings for the current HR user
 * @param limit - Optional number of jobs to return (default: 5)
 * @returns Promise with job listings response
 */
export const getHRJobListings = async (limit = 5, page = 1): Promise<any> => {
  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return {
        success: false,
        message: "Authentication required",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/jobs/hr/listings`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      params: { limit, page },
      timeout: 10000,
    });

    return {
      success: true,
      data: response.data,
      status: response.status,
      meta: response.data.meta || null,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Request timed out"
          : "Failed to fetch job listings"),
      error: err,
    };
  }
};

// Candidate search types
export interface CandidateUser {
  id: string;
  username: string;
  email: string;
  role: string;
  first_name: string;
  last_name: string;
  created_at: string;
  cv_id: string;
}

export interface CVSummaryData {
  years_of_experience: number;
  relevant_tags: string[];
  highest_education: string;
}

export interface WorkExperience {
  role: string;
  institution: string;
  period: string;
  location: string;
  description: string;
}

export interface Education {
  degree: string;
  institution: string;
  period: string;
  location: string;
  description: string;
  relevant_courses: string[];
}

export interface Project {
  name: string;
  role: string;
  year: string;
  description: string;
  github_link: string;
  live_demo: string;
  tech_stack: string[];
}

export interface Publication {
  title: string;
  date: string;
  publisher: string;
  type: string;
  link: string;
  description: string;
}

export interface Award {
  name: string;
  institution: string;
  date: string;
  description: string;
}

export interface CVData {
  CV_data: {
    name: string;
    position: string;
    professional_summary: string;
    email: string;
    phone: string;
    location: string;
    skills: string[];
    work_experience: WorkExperience[];
    education: Education[];
    certifications: any[];
    awards: Award[];
    publications: Publication[];
    projects: Project[];
  };
  summary_data: CVSummaryData;
  pdf_path: string | null;
  uploaded_at: string | null;
  profile_picture_path: string | null;
}

export interface CandidateSearchResponse {
  success: boolean;
  data?: {
    code: number;
    message: string;
    data: {
      candidates: Candidate[];
    };
    errors: string[] | null;
    meta: any | null;
  };
  status?: number;
  message?: string;
  error?: any;
}

export interface SearchCandidatesRequest {
  page: number;
  limit: number;
  job_title: string;
  job_description: string;
  only_relevant?: boolean;
  filters?: SearchCandidateFilter;
}

/**
 * Search for candidates with optional filters
 * @param searchData - Search data containing job title and description
 * @returns Promise with candidate search response
 */
export const searchCandidates = async (
  searchData: SearchCandidatesRequest
): Promise<CandidateSearchResponse> => {
  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return {
        success: false,
        message: "Authentication required",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/search/candidates`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      data: {
        job_title: searchData.job_title,
        job_description: searchData.job_description,
        page: searchData.page,
        top_k: searchData.limit,
        only_relevant: searchData.only_relevant,
        skill_filter: searchData.filters?.skills || "",
        field_of_study_filter: searchData.filters?.field_of_study || "",
        companies_filter: searchData.filters?.company || "",
        work_type_filter: searchData.filters?.work_type || "",
        location_filter: searchData.filters?.location || "",
        degree_filter: searchData.filters?.degree || "",
        country_filter: searchData.filters?.country || "",
        universities_filter: searchData.filters?.university || "",
        titles_filter: searchData.filters?.title || "",
        min_experience: Number(searchData.filters?.min_experience) || 0,
        max_experience: Number(searchData.filters?.max_experience) || 40,
        candidate_type: (searchData.filters?.candidate_type || []).join(",") || "",
        candidate_saved: searchData.filters?.saved_candidates || [],
        shortlisted_candidates: searchData.filters?.shortlisted || false,
        completed_interview: searchData.filters?.completed_interview || false,
        ai_interview_status: (searchData.filters?.ai_interview_status || []).join(",") || "",
      },
    });

    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Request timed out"
          : "Failed to search candidates"),
      error: err,
    };
  }
};

/**
 * Search for candidates with optional filters
 * @param searchData - Search data containing job title and description
 * @returns Promise with candidate search response
 */
export const getCandidate = async (candidate_id: string): Promise<any> => {
  try {
    const authToken = getCookie("authToken");

    if (!authToken) {
      return {
        success: false,
        message: "Authentication required",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/employer/candidate/${candidate_id}`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      timeout: 15000, // 15 second timeout
    });

    return response;
  } catch (error) {
    console.error("failed to get candidate by id", error);
    return undefined;
  }
};

/**
 * Dummy function to return all candidates
 * @param searchData - Search data containing job title and description
 * @returns Promise with candidate search response
 */
export const searchAllCandidates = async (
  searchData: SearchCandidatesRequest
): Promise<Candidate[]> => {
  try {
    const authToken = getCookie("authToken");

    const response: AxiosResponse = await axios({
      url: `${apiURL}/search/all_candidates`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      data: {
        job_title: searchData.job_title,
        job_description: searchData.job_description,
        ...searchData.filters,
      },
      timeout: 15000, // 15 second timeout
    });
    console.log(response.data.data.candidates);
    return response.data.data.candidates.map((elem: any) => {
      return {
        ...elem.cv_data.CV_data,
      };
    }) as Candidate[];
  } catch (error) {
    const err = error as AxiosError;

    return [];
  }
};

export const uploadCompanyProfile = async (
  file: File
): Promise<{
  success: boolean;
  status: number;
  data?: string;
  message?: string;
  error?: AxiosError;
}> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      return {
        success: false,
        status: 401,
        message: "No authentication token found",
      };
    }

    // Create FormData object to send file
    const formData = new FormData();
    formData.append("file", file);
    console.log("formData", formData);

    const response: AxiosResponse = await axios({
      url: `${apiURL}/employer/upload-company-logo`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: formData,
      timeout: 30000, // 30 second timeout for image upload
    });

    return {
      success: true,
      status: response.data.code,
      data: response.data.data.company_logo_path,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    if (error instanceof Error) {
      catchErrorLog(`failed to upload company logo, err: ${error.message}`);
    }

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Upload request timed out"
          : "Failed to upload profile picture"),
      error: err,
    };
  }
};

export const sendCustomizedEmail = async (
  body: SendCustomizedEmailRequest
): Promise<SendCustomizedEmailResponse> => {
  try {
    const token = getCookie("authToken");
    if (!token) {
      return {
        code: 401,
        message: "No authentication token found",
      };
    }

    // Create FormData object to send file
    const formData = new FormData();
    formData.append("reply_to", body.reply_to || "");
    if (body.user_ids) {
      formData.append("user_ids", body.user_ids ? body.user_ids.join(",") : "");
    }
    // formData.append("to_email", body.to_email);
    if (body.cc) {
      formData.append("cc", body.cc);
    }
    formData.append("sender_name", body.sender_name);
    formData.append("email_subject", body.email_subject);
    formData.append("email_body", body.email_body);
    formData.append("hr_name", "");
    formData.append("company_name", "ourteam");
    formData.append("job_id", body.job_id);
    if (body.applicant_ids) {
      formData.append("applicant_ids", body.applicant_ids.join(","));
    }
    formData.append(
      "interview_invitation",
      body.interview_invitation.toString()
    );
    formData.append("reject_candidate", body.reject_candidate.toString());

    if (body.attachment && body.attachment instanceof File) {
      formData.append("attachment", body.attachment);
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/employer/send-customized-email`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: formData,
      timeout: 30000, // 30 second timeout for image upload
    });

    return {
      code: response.data.code,
      message: response.data.message,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      code: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Request timed out"
          : "Failed to send invitation"),
    };
  }
};

export const checkHRViewLimit = async (candidate_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/search/check-hr-view-limit`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: { candidate_id },
    timeout: 30000, // 30 second timeout for image upload
  });
};

export const getCalculateMultilevelSimilarityScore = async (params: CalculateSimiliarityScoreParams): Promise<any> => {
  const token = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/employer/calculate-multilevel-similarity-score`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: params,
  });
};


export const getEmailTemplates = async (): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/employer/list-email-template`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export const createEmailTemplate = async (payload: EmailTemplateRequest): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/employer/create-email-template`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: payload
  });
}

export const updateEmailTemplate = async (template_id: string, payload: EmailTemplateRequest): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/employer/email-template/${template_id}`,
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: payload
  });
}

export const upsertDefaultEmailTemplate = async (template_key: string, payload: EmailTemplateRequest): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/employer/default-email-template/${template_key}`,
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: payload
  });
}

export const deleteEmailTemplate = async (template_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/employer/email-template/${template_id}`,
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export const sendCustomizedEmailV2 = async (
  body: SendCustomizedEmailRequest
): Promise<any> => {
  const token = getCookie("authToken");
  // Create FormData object to send file
  const formData = new FormData();
  formData.append("reply_to", body.reply_to || "");
  if (body.user_ids) {
    formData.append("user_ids", body.user_ids ? body.user_ids.join(",") : "");
  }
  if (body.cc) {
    formData.append("cc", body.cc);
  }
  formData.append("sender_name", body.sender_name);
  formData.append("email_subject", body.email_subject);
  formData.append("email_body", body.email_body);
  formData.append("job_id", body.job_id);
  if (body.applicant_ids) {
    formData.append("applicant_ids", body.applicant_ids.join(","));
  }
  formData.append(
    "interview_invitation",
    body.interview_invitation.toString()
  );
  formData.append("reject_candidate", body.reject_candidate.toString());
  if (body.assessment_invitation !== undefined) {
    formData.append("assessment_invitation", body.assessment_invitation.toString());
    formData.append("assessment_invitation_type", body.assessment_invitation_type || "")
  }
  if (body.internal_notes) {
    formData.append("internal_notes", body.internal_notes);
  }
  if (body.notify_job_closed !== undefined) {
    formData.append("notify_job_closed", body.notify_job_closed.toString());
  }
  if (body.review_schedule_notes) {
    formData.append("review_schedule_notes", JSON.stringify(body.review_schedule_notes));
  }

  if (body.attachment && body.attachment instanceof File) {
    formData.append("attachment", body.attachment);
  }

  return await axios({
    url: `${apiURL}/employer/v2/send-customized-email`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: formData,
  });
};

export const createWorkspaceCompany = async (
  body: WorkspaceCompanyRequest
): Promise<any> => {
  const token = getCookie("authToken");
  // Create FormData object to send file
  const formData = new FormData();
  if (body.company_logo) {
    formData.append("company_logo", body.company_logo);
  }
  formData.append("first_name", body.first_name);
  formData.append("last_name", body.last_name);
  formData.append("email", body.email);
  formData.append("company_name", body.company_name);
  formData.append("workspace_url", body.workspace_url);
  formData.append("workspace_access_type", body.workspace_access_type.toString());
  formData.append("workspace_access_duration", body.workspace_access_duration.toString());
  if (body.workspace_product_type) {
    formData.append("workspace_product_type", body.workspace_product_type);
  }
  if (body.mock_job_id) {
    formData.append("mock_job_id", body.mock_job_id);
  }
  if (body.mock_assessment_id) {
    formData.append("mock_assessment_id", body.mock_assessment_id);
  }
  if (body.mock_form_id) {
    formData.append("mock_form_id", body.mock_form_id);
  }

  return await axios({
    url: `${apiURL}/employer/hr/create`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: formData,
  });
};

export const editWorkspaceCompany = async (
  company_id: string,
  body: WorkspaceCompanyRequest
): Promise<any> => {
  const token = getCookie("authToken");
  // Create FormData object to send file
  const formData = new FormData();
  if (body.company_logo) {
    formData.append("company_logo", body.company_logo);
  }
  formData.append("first_name", body.first_name);
  formData.append("last_name", body.last_name);
  formData.append("email", body.email);
  formData.append("company_name", body.company_name);
  formData.append("workspace_url", body.workspace_url);
  formData.append("workspace_access_type", body.workspace_access_type.toString());
  formData.append("workspace_access_duration", body.workspace_access_duration.toString());
  if (body.workspace_product_type) {
    formData.append("workspace_product_type", body.workspace_product_type);
  }
  if (body.mock_job_id) {
    formData.append("mock_job_id", body.mock_job_id);
  }
  if (body.mock_assessment_id) {
    formData.append("mock_assessment_id", body.mock_assessment_id);
  }
  if (body.mock_form_id) {
    formData.append("mock_form_id", body.mock_form_id);
  }

  return await axios({
    url: `${apiURL}/employer/hr/${company_id}/edit`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: formData,
  });
};

export const getListWorkspaceCompany = async (params: WorkSpaceCompanyListRequest): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/employer/hr/list-workspace`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: params,
  });
}

export const updateWorkspaceStatus = async (company_id: string, status: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/employer/hr/update-status-workspace`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      company_id,
      action: status
    }
  });
}

export const getDetailWorkspaceCompany = async (company_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/employer/hr/workspace-detail/${company_id}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export const activateWorkspaceCompany = async (company_id: string): Promise<any> => {
  const token = getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/employer/activate-workspace`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      company_id
    }
  });
}

export const resendActivationLink = async (company_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/employer/hr/resend-activation-link`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      company_id,
    }
  });
}